import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from './Card'
import Button from './Button'
import { Lock, Sparkles, X, Check, ArrowRight } from 'lucide-react'
import { getSubscriptionTiers, getTierFeatures } from '../services/featureGating'
import { initiatePayment } from '../services/payments'

const UpgradePrompt = ({ featureCode, onClose }) => {
  const [tiers, setTiers] = useState([])
  const [selectedTier, setSelectedTier] = useState(null)
  const [tierFeatures, setTierFeatures] = useState([])
  const [loading, setLoading] = useState(true)
  const [upgrading, setUpgrading] = useState(false)
  const organizationId = useOrganizationId()
  const { organization, subscription } = useOrganization()

  useEffect(() => {
    fetchTiers()
  }, [])

  useEffect(() => {
    if (selectedTier) {
      fetchTierFeatures(selectedTier.id)
    }
  }, [selectedTier])

  const fetchTiers = async () => {
    try {
      setLoading(true)
      const data = await getSubscriptionTiers()
      setTiers(data)
      // Auto-select first tier that has the feature (if available)
      if (data.length > 0) {
        setSelectedTier(data[0])
      }
    } catch (error) {
      console.error('Error fetching tiers:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTierFeatures = async (tierId) => {
    try {
      const data = await getTierFeatures(tierId)
      setTierFeatures(data.map((tf) => tf.feature))
    } catch (error) {
      console.error('Error fetching tier features:', error)
    }
  }

  const handleUpgrade = async () => {
    if (!selectedTier || !organizationId) return

    try {
      setUpgrading(true)
      // Initiate payment
      await initiatePayment(organizationId, selectedTier.id, 'monthly')
      // Payment will redirect to Paystack
    } catch (error) {
      alert(error.message || 'Failed to initiate upgrade')
    } finally {
      setUpgrading(false)
    }
  }

  const getFeatureInfo = () => {
    const featureNames = {
      advanced_analytics: 'Advanced Analytics',
      bulk_operations: 'Bulk Operations',
      api_access: 'API Access',
      white_label: 'White Label',
      priority_support: 'Priority Support',
      advanced_backups: 'Advanced Backups',
      disaster_recovery: 'Disaster Recovery',
      custom_integrations: 'Custom Integrations',
      unlimited_users: 'Unlimited Users',
      unlimited_clients: 'Unlimited Clients',
      advanced_security: 'Advanced Security',
      gdpr_tools: 'GDPR Tools',
      audit_logs: 'Audit Logs',
      live_session_recording: 'Live Session Recording',
    }
    return featureNames[featureCode] || featureCode
  }

  if (loading) {
    return (
      <Card variant="soft" className="p-8 text-center">
        <p className="text-gray-500">Loading upgrade options...</p>
      </Card>
    )
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
      >
        <Card variant="glass" className="p-6 relative">
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          )}

          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center">
              <Lock size={32} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Upgrade Required</h3>
            <p className="text-gray-600">
              This feature requires a higher subscription tier. Upgrade to access{' '}
              <strong>{getFeatureInfo()}</strong> and more.
            </p>
          </div>

          {/* Current Plan */}
          {subscription && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Current Plan:</strong> {subscription.tier?.name || 'Free'} •{' '}
                {subscription.status === 'trial' && 'Trial ends: '}
                {subscription.trial_end
                  ? new Date(subscription.trial_end).toLocaleDateString()
                  : 'No trial'}
              </p>
            </div>
          )}

          {/* Subscription Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {tiers.map((tier) => (
              <Card
                key={tier.id}
                variant="soft"
                className={`p-4 cursor-pointer transition-all ${
                  selectedTier?.id === tier.id
                    ? 'ring-2 ring-primary-500 bg-primary-50'
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => setSelectedTier(tier)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-gray-900">{tier.name}</h4>
                  {selectedTier?.id === tier.id && (
                    <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-3">{tier.description}</p>
                <div className="mb-3">
                  <span className="text-2xl font-bold text-gray-900">
                    ${tier.price_monthly}
                  </span>
                  <span className="text-sm text-gray-600">/month</span>
                </div>
                {tier.price_yearly > 0 && (
                  <p className="text-xs text-gray-500 mb-3">
                    ${tier.price_yearly}/year (save ${(tier.price_monthly * 12 - tier.price_yearly).toFixed(0)})
                  </p>
                )}
                <div className="text-xs text-gray-600 space-y-1">
                  {tier.max_users && <p>Up to {tier.max_users} users</p>}
                  {tier.max_clients && <p>Up to {tier.max_clients} clients</p>}
                  {tier.max_storage_gb && <p>{tier.max_storage_gb} GB storage</p>}
                </div>
              </Card>
            ))}
          </div>

          {/* Selected Tier Features */}
          {selectedTier && tierFeatures.length > 0 && (
            <Card variant="soft" className="p-4 mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">
                Features included in {selectedTier.name}:
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {tierFeatures.map((feature) => (
                  <div key={feature.id} className="flex items-center gap-2 text-sm">
                    <Check size={16} className="text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature.name}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Upgrade Button */}
          <div className="flex gap-3">
            <Button
              onClick={handleUpgrade}
              disabled={!selectedTier || upgrading}
              className="flex-1"
            >
              {upgrading ? (
                <>
                  <Sparkles size={18} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Upgrade to {selectedTier?.name || 'Plan'}
                  <ArrowRight size={18} className="ml-2" />
                </>
              )}
            </Button>
            {onClose && (
              <Button variant="secondary" onClick={onClose}>
                Maybe Later
              </Button>
            )}
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            Secure payment powered by Paystack. Your subscription will activate immediately.
          </p>
        </Card>
      </motion.div>
    </AnimatePresence>
  )
}

export default UpgradePrompt

